// Function index:
//   WS            — { send, on, off, onReconnect } client wrapper around a browser WebSocket.
//   connectWS(url) — open a reconnecting WS and return a WS controller.
//
// Tiny typed WebSocket wrapper with reconnect.
// Server sends JSON messages of shape { type: string, ...payload }.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Handler = (payload: any) => void;

export interface WS {
  // Returns whether the frame actually left the machine — a closed socket
  // swallows nothing silently; callers surface the failure to the player.
  send(type: string, payload?: unknown): boolean;
  on(type: string, handler: Handler): void;
  // NM-D3 needs an off() so the world-change teardown can drop the
  // scene-bound handlers — otherwise every remount accumulates dead
  // closures that throw on the next event (the scene they captured is
  // destroyed).
  off(type: string, handler: Handler): void;
  // Fires on every re-open AFTER a drop (never on the first open). Events
  // sent while the socket was down are gone — the runtime re-syncs from a
  // fresh bootstrap here. A policy close (1008: signed out / removed from
  // the party) does NOT reconnect; it fires the synthetic 'ws.dead' event
  // through on() instead.
  onReconnect(handler: () => void): void;
}

export function connectWS(url: string): WS {
  const handlers = new Map<string, Set<Handler>>();
  const reconnectHandlers = new Set<() => void>();
  let socket: WebSocket | null = null;
  let backoff = 500;
  let everOpened = false;

  const open = () => {
    socket = new WebSocket(url);
    socket.addEventListener('open', () => {
      backoff = 500;
      if (everOpened) reconnectHandlers.forEach((h) => h());
      everOpened = true;
    });
    socket.addEventListener('message', (ev) => {
      try {
        const { type, ...rest } = JSON.parse(ev.data);
        handlers.get(type)?.forEach((h) => h(rest));
      } catch { /* ignore non-JSON */ }
    });
    socket.addEventListener('close', (ev) => {
      // 1008 = policy violation (logged out, removed from the party): the
      // server will refuse every retry, so reconnecting forever just hammers
      // it with a repeating rejection line. Surface a synthetic ws.dead event
      // instead — the runtime tells the player to reload and sign in.
      if (ev.code === 1008) {
        handlers.get('ws.dead')?.forEach((h) => h({ code: ev.code, reason: ev.reason }));
        return;
      }
      setTimeout(open, backoff);
      backoff = Math.min(backoff * 2, 10000);
    });
  };
  open();

  return {
    send(type, payload: Record<string, unknown> = {}) {
      if (socket?.readyState !== WebSocket.OPEN) return false;
      socket.send(JSON.stringify({ type, ...payload }));
      return true;
    },
    on(type, handler) {
      if (!handlers.has(type)) handlers.set(type, new Set());
      handlers.get(type)!.add(handler);
    },
    off(type, handler) {
      const set = handlers.get(type);
      if (!set) return;
      set.delete(handler);
      if (set.size === 0) handlers.delete(type);
    },
    onReconnect(handler) {
      reconnectHandlers.add(handler);
    },
  };
}
